import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../context/NotificationContext";
import {
  createAdminSolution,
  deleteAdminSolution,
  getAdminSolutionById,
  getAdminSolutions,
  updateAdminSolution,
} from "../../../api/solutionsAdmin";
import { resolveMediaUrl } from "../../../utils/media";
import AdminProductPdfsPanel from "./AdminProductPdfsPanel";

const emptyTab = () => ({ label: "", tabImageUrl: "" });
const emptyProduct = () => ({ title: "", description: "", imageUrl: "" });
const emptyFeature = () => ({ title: "", description: "" });

const EMPTY_FORM = {
  solutionKey: "",
  language: "en",
  heroTag: "",
  title: "",
  introDescription: "",
  cardDescription: "",
  cardIconUrl: "",
  heroDescription: "",
  heroImageUrl: "",
  specificationsLabel: "",
  descriptionHeading: "",
  descriptionBody: "",
  productsTag: "",
  productsTitle: "",
  productsTitleAccent: "",
  featuresTag: "",
  featuresTitle: "",
  featuresTitleAccent: "",
  isActive: true,
  descriptionTabs: [emptyTab()],
  products: [emptyProduct()],
  features: [emptyFeature()],
};

const getSolutionName = (solution) =>
  solution?.title ||
  solution?.solutionKey ||
  solution?.heroTag ||
  solution?.slug ||
  solution?.id ||
  "Solution";

const getSolutionHeroImageCandidate = (solution) =>
  solution?.heroImageUrl ||
  solution?.heroImage ||
  solution?.imageUrl ||
  solution?.image ||
  solution?.mediaUrl ||
  solution?.photo ||
  "";

const getSolutionCardIconCandidate = (solution) =>
  solution?.cardIconUrl ||
  solution?.cardIcon ||
  solution?.iconUrl ||
  solution?.icon ||
  "";

const mapSolutionToForm = (solution) => ({
  solutionKey: solution?.solutionKey || "",
  language: String(solution?.language || "en").toLowerCase(),
  heroTag: solution?.heroTag || "",
  title: solution?.title || "",
  introDescription: solution?.introDescription || "",
  cardDescription: solution?.cardDescription || "",
  cardIconUrl: solution?.cardIconUrl || "",
  heroDescription: solution?.heroDescription || "",
  heroImageUrl: getSolutionHeroImageCandidate(solution),
  specificationsLabel: solution?.specificationsLabel || "",
  descriptionHeading: solution?.descriptionHeading || "",
  descriptionBody: solution?.descriptionBody || "",
  productsTag: solution?.productsTag || "",
  productsTitle: solution?.productsTitle || "",
  productsTitleAccent: solution?.productsTitleAccent || "",
  featuresTag: solution?.featuresTag || "",
  featuresTitle: solution?.featuresTitle || "",
  featuresTitleAccent: solution?.featuresTitleAccent || "",
  isActive:
    typeof solution?.isActive === "boolean"
      ? solution.isActive
      : typeof solution?.active === "boolean"
      ? solution.active
      : true,
  descriptionTabs: Array.isArray(solution?.descriptionTabs)
    ? solution.descriptionTabs.map((it) => ({
        label: it?.label || "",
        tabImageUrl: it?.tabImageUrl || "",
      }))
    : [emptyTab()],
  products: Array.isArray(solution?.products)
    ? solution.products.map((it) => ({
        title: it?.title || "",
        description: it?.description || "",
        imageUrl: it?.imageUrl || "",
      }))
    : [emptyProduct()],
  features: Array.isArray(solution?.features)
    ? solution.features.map((it) => ({
        title: it?.title || "",
        description: it?.description || "",
      }))
    : [emptyFeature()],
});

const toPayload = (form, { isEdit = false } = {}) => {
  const payload = {
    solutionKey: form.solutionKey.trim(),
    language: String(form.language || "en").toLowerCase(),
    heroTag: form.heroTag.trim(),
    title: form.title.trim(),
    introDescription: form.introDescription.trim(),
    cardDescription: form.cardDescription.trim(),
    cardIconUrl: form.cardIconUrl.trim(),
    heroDescription: form.heroDescription.trim(),
    heroImageUrl: form.heroImageUrl.trim(),
    specificationsLabel: form.specificationsLabel.trim(),
    descriptionHeading: form.descriptionHeading.trim(),
    descriptionBody: form.descriptionBody.trim(),
    productsTag: form.productsTag.trim(),
    productsTitle: form.productsTitle.trim(),
    productsTitleAccent: form.productsTitleAccent.trim(),
    featuresTag: form.featuresTag.trim(),
    featuresTitle: form.featuresTitle.trim(),
    featuresTitleAccent: form.featuresTitleAccent.trim(),
    isActive: Boolean(form.isActive),
    descriptionTabs: (form.descriptionTabs || [])
      .map((it) => ({
        label: String(it?.label || "").trim(),
        tabImageUrl: String(it?.tabImageUrl || "").trim(),
      }))
      .filter((it) => it.label || it.tabImageUrl),
    products: (form.products || [])
      .map((it) => ({
        title: String(it?.title || "").trim(),
        description: String(it?.description || "").trim(),
        imageUrl: String(it?.imageUrl || "").trim(),
      }))
      .filter((it) => it.title || it.description || it.imageUrl),
    features: (form.features || [])
      .map((it) => ({
        title: String(it?.title || "").trim(),
        description: String(it?.description || "").trim(),
      }))
      .filter((it) => it.title || it.description),
  };

  if (isEdit) {
    // Backend supports partial updates, but arrays replace fully when sent.
    // We send the complete edited object to keep behavior deterministic.
    return payload;
  }
  return payload;
};

const SectionArrayEditor = ({
  label,
  items,
  onChange,
  itemFactory,
  renderItem,
  addText,
}) => (
  <div className="rounded-xl border border-slate-200 p-3">
    <p className="text-sm font-semibold text-[#011337] mb-2">{label}</p>
    {items.map((item, idx) => (
      <div key={`${label}-${idx}`} className="mb-2">
        {renderItem(item, idx, (nextItem) => {
          onChange(items.map((it, i) => (i === idx ? nextItem : it)));
        })}
        <button
          type="button"
          onClick={() => onChange(items.filter((_, i) => i !== idx))}
          className="mt-1 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
        >
          Remove
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange([...items, itemFactory()])}
      className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
    >
      {addText}
    </button>
  </div>
);

const SolutionsManager = () => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const loadSolutions = async () => {
    setLoading(true);
    try {
      const data = await getAdminSolutions();
      setSolutions(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load solutions",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSolutions();
  }, []);

  const refreshSolutionRowById = async (id) => {
    if (id == null) {
      await loadSolutions();
      return;
    }
    try {
      const one = await getAdminSolutionById(id);
      setSolutions((prev) => {
        const idx = prev.findIndex((x) => Number(x?.id) === Number(id));
        if (idx === -1) return one ? [...prev, one] : prev;
        const next = [...prev];
        next[idx] = one;
        return next;
      });
    } catch {
      await loadSolutions();
    }
  };

  const sortedSolutions = useMemo(() => {
    const currentLang = String(i18n.language || "en").toLowerCase().split("-")[0];
    const filtered = solutions.filter((solution) => {
      const lang = String(solution?.language || "en").toLowerCase().split("-")[0];
      return lang === currentLang;
    });
    return [...filtered].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [solutions, i18n.language]);
  const editingSolution = useMemo(
    () => solutions.find((x) => Number(x?.id) === Number(editingId)) || null,
    [solutions, editingId]
  );

  const handleCreate = async () => {
    if (!createForm.solutionKey.trim() || !createForm.title.trim()) {
      notify({ type: "error", title: "Solution key and title are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = toPayload(createForm);
      await createAdminSolution(payload);
      notify({ type: "success", title: "Solution created" });
      setCreateForm(EMPTY_FORM);
      await loadSolutions();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = async (solution) => {
    setEditingId(solution.id);
    setEditForm(mapSolutionToForm(solution));
    try {
      const fresh = await getAdminSolutionById(solution.id);
      setSolutions((prev) => prev.map((x) => (Number(x.id) === Number(solution.id) ? fresh : x)));
      setEditForm(mapSolutionToForm(fresh));
    } catch {
      /* keep list row */
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.solutionKey.trim() || !editForm.title.trim()) {
      notify({ type: "error", title: "Solution key and title are required" });
      return;
    }
    try {
      const payload = toPayload(editForm, { isEdit: true });
      await updateAdminSolution({ id: editingId, payload });
      notify({ type: "success", title: "Solution updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      await loadSolutions();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleDelete = async (solution) => {
    const ok = window.confirm(`Delete solution "${getSolutionName(solution)}"?`);
    if (!ok) return;
    try {
      await deleteAdminSolution({ id: solution.id });
      notify({ type: "success", title: "Solution deleted" });
      await loadSolutions();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const renderForm = (form, setForm) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">
        Solution Key
        <input
          type="text"
          value={form.solutionKey}
          onChange={(e) => setForm((prev) => ({ ...prev, solutionKey: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          placeholder="exterior-system"
        />
      </label>
      <label className="text-sm text-slate-700">
        Language
        <select
          value={form.language}
          onChange={(e) => setForm((prev) => ({ ...prev, language: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="en">EN</option>
          <option value="ar">AR</option>
          <option value="ku">KU</option>
        </select>
      </label>
      <label className="text-sm text-slate-700">
        Hero Tag
        <input
          type="text"
          value={form.heroTag}
          onChange={(e) => setForm((prev) => ({ ...prev, heroTag: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Title
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Intro Description
        <textarea
          value={form.introDescription}
          onChange={(e) => setForm((prev) => ({ ...prev, introDescription: e.target.value }))}
          className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Card Description
        <textarea
          value={form.cardDescription}
          onChange={(e) => setForm((prev) => ({ ...prev, cardDescription: e.target.value }))}
          className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Card Icon URL
        <input
          type="text"
          value={form.cardIconUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, cardIconUrl: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Hero Image URL
        <input
          type="text"
          value={form.heroImageUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, heroImageUrl: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Hero Description
        <textarea
          value={form.heroDescription}
          onChange={(e) => setForm((prev) => ({ ...prev, heroDescription: e.target.value }))}
          className="mt-1 w-full min-h-[80px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Specifications Label
        <input
          type="text"
          value={form.specificationsLabel}
          onChange={(e) => setForm((prev) => ({ ...prev, specificationsLabel: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Description Heading
        <input
          type="text"
          value={form.descriptionHeading}
          onChange={(e) => setForm((prev) => ({ ...prev, descriptionHeading: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Description Body
        <textarea
          value={form.descriptionBody}
          onChange={(e) => setForm((prev) => ({ ...prev, descriptionBody: e.target.value }))}
          className="mt-1 w-full min-h-[80px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Products Tag
        <input
          type="text"
          value={form.productsTag}
          onChange={(e) => setForm((prev) => ({ ...prev, productsTag: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Products Title
        <input
          type="text"
          value={form.productsTitle}
          onChange={(e) => setForm((prev) => ({ ...prev, productsTitle: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Products Title Accent
        <input
          type="text"
          value={form.productsTitleAccent}
          onChange={(e) => setForm((prev) => ({ ...prev, productsTitleAccent: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Features Tag
        <input
          type="text"
          value={form.featuresTag}
          onChange={(e) => setForm((prev) => ({ ...prev, featuresTag: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Features Title
        <input
          type="text"
          value={form.featuresTitle}
          onChange={(e) => setForm((prev) => ({ ...prev, featuresTitle: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="text-sm text-slate-700">
        Features Title Accent
        <input
          type="text"
          value={form.featuresTitleAccent}
          onChange={(e) => setForm((prev) => ({ ...prev, featuresTitleAccent: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
        />
        Active
      </label>

      <div className="sm:col-span-2 grid gap-3">
        <SectionArrayEditor
          label="Description Tabs"
          items={form.descriptionTabs}
          onChange={(next) => setForm((prev) => ({ ...prev, descriptionTabs: next.length ? next : [emptyTab()] }))}
          itemFactory={emptyTab}
          addText="Add tab"
          renderItem={(item, _idx, onItemChange) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={item.label}
                onChange={(e) => onItemChange({ ...item, label: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Tab label"
              />
              <input
                type="text"
                value={item.tabImageUrl}
                onChange={(e) => onItemChange({ ...item, tabImageUrl: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="/uploads/solutions/exteriortab1.jpeg"
              />
            </div>
          )}
        />

        <SectionArrayEditor
          label="Products"
          items={form.products}
          onChange={(next) => setForm((prev) => ({ ...prev, products: next.length ? next : [emptyProduct()] }))}
          itemFactory={emptyProduct}
          addText="Add product"
          renderItem={(item, _idx, onItemChange) => (
            <div className="grid sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={item.title}
                onChange={(e) => onItemChange({ ...item, title: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Title"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => onItemChange({ ...item, description: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Description"
              />
              <input
                type="text"
                value={item.imageUrl}
                onChange={(e) => onItemChange({ ...item, imageUrl: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="/uploads/solutions/fire.png"
              />
            </div>
          )}
        />

        <SectionArrayEditor
          label="Features"
          items={form.features}
          onChange={(next) => setForm((prev) => ({ ...prev, features: next.length ? next : [emptyFeature()] }))}
          itemFactory={emptyFeature}
          addText="Add feature"
          renderItem={(item, _idx, onItemChange) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={item.title}
                onChange={(e) => onItemChange({ ...item, title: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Feature title"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => onItemChange({ ...item, description: e.target.value })}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Feature description"
              />
            </div>
          )}
        />
      </div>
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Solutions Manager</h2>
            <p className="text-sm text-slate-500 mt-1">Create and edit solution details used by solution pages.</p>
          </div>
          <button
            type="button"
            onClick={loadSolutions}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>

        {renderForm(createForm, setCreateForm)}

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create solution"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-[8%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="w-[18%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Key</th>
              <th className="w-[18%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
              <th className="w-[10%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Lang</th>
              <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Card Icon</th>
              <th className="w-[16%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Hero Image</th>
              <th className="w-[16%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-sm text-slate-500">Loading solutions...</td>
              </tr>
            ) : sortedSolutions.length ? (
              sortedSolutions.map((solution) => {
                const cardIcon = resolveMediaUrl(getSolutionCardIconCandidate(solution));
                const heroImage = resolveMediaUrl(getSolutionHeroImageCandidate(solution));
                return (
                  <tr key={solution.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{solution.id}</td>
                    <td className="px-3 py-3 text-sm text-slate-900">{solution.solutionKey || "-"}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{getSolutionName(solution)}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">{String(solution.language || "en").toUpperCase()}</td>
                    <td className="px-3 py-3">
                      {cardIcon ? (
                        <img src={cardIcon} alt="" className="w-14 h-14 object-contain rounded border border-slate-200 p-1" />
                      ) : (
                        <p className="text-xs text-slate-500">No icon</p>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {heroImage ? (
                        <img src={heroImage} alt="" className="w-28 h-20 object-cover rounded border border-slate-200" />
                      ) : (
                        <p className="text-xs text-slate-500">No image</p>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(solution)}
                          className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(solution)}
                          className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-sm text-slate-500">No solutions found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Solution #{editingId}</h3>
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setEditForm(EMPTY_FORM);
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          {renderForm(editForm, setEditForm)}
          <AdminProductPdfsPanel
            variant="solution"
            productId={editingId}
            productSlug={String(editingSolution?.solutionKey ?? editForm?.solutionKey ?? "").trim()}
            pdfDocuments={editingSolution?.pdfDocuments || editingSolution?.pdfs || []}
            onRefresh={() => refreshSolutionRowById(editingId)}
          />

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={saveEdit}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm"
            >
              Save changes
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SolutionsManager;
